/**
 * Locale selection for tenant-authored content with an English fallback.
 *
 * Tenant-authored columns come in pairs (`title`/`title_ar`,
 * `content`/`content_ar`, `about_page_data`/`about_page_data_ar`). The Arabic
 * side is optional: most tenants have not written it, and a half-migrated page
 * must not render blank. So the rule everywhere is "Arabic when the locale is
 * Arabic AND the Arabic value is actually present, otherwise English".
 *
 * Kept in one place because the rule is easy to get subtly wrong per call site
 * — an empty string is falsy but `''` returned from a text column is a real,
 * common value, and `??` would happily hand it to the renderer.
 */

/** True when the locale should render right-to-left Arabic content. */
export function isArabicLocale(locale: string | undefined | null): boolean {
  return (locale ?? '').toLowerCase().startsWith('ar')
}

/** Non-empty after trimming. Guards against whitespace-only CMS values. */
function hasText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Pick the Arabic string when the locale is Arabic and the value is non-empty,
 * else the English one.
 */
export function pickLocalizedText(
  locale: string | undefined | null,
  en: string | null | undefined,
  ar: string | null | undefined
): string {
  if (isArabicLocale(locale) && hasText(ar)) return ar
  return en ?? ''
}

/** Any master-data row carrying the standard bilingual name pair. */
export interface LocalizedNameRow {
  name_en?: string | null
  name_ar?: string | null
}

/**
 * {@link pickLocalizedText} for the `name_en` / `name_ar` pair that every piece
 * of master data carries — stores, items, suppliers, categories, units, brands,
 * asset classes, reason codes.
 *
 * A thin delegation, not a second implementation: the "Arabic only when the
 * locale is Arabic AND the value is really there" rule stays in one place. It
 * exists because the shop screens repeat this on hundreds of rows and the
 * hand-rolled idiom `(locale === 'ar' ? r.name_ar : null) || r.name_en` was
 * being copied with subtle variations — some of which let a whitespace-only
 * `name_ar` win and render a blank cell.
 *
 * ⛔ Names only. Codes, SKUs and document numbers (`code`, `reference_number`,
 * GRN/PO numbers) are identifiers, not text — they are never localized.
 *
 * @param locale - dashboard locale tag ('en' | 'ar').
 * @param row - anything with `name_en`/`name_ar`; null/undefined yields ''.
 */
export function pickLocalizedName(
  locale: string | undefined | null,
  row: LocalizedNameRow | null | undefined
): string {
  if (!row) return ''
  return pickLocalizedText(locale, row.name_en, row.name_ar)
}

/**
 * Object twin of {@link pickLocalizedText}, for jsonb pairs such as
 * `about_page_data` / `about_page_data_ar`. An empty object counts as absent,
 * so a tenant who saved `{}` still gets the English content rather than a page
 * of blank sections.
 */
export function pickLocalizedObject<T extends object>(
  locale: string | undefined | null,
  en: T | null | undefined,
  ar: T | null | undefined
): T | null {
  if (isArabicLocale(locale) && ar && Object.keys(ar).length > 0) return ar
  return en ?? null
}
